import useUser from '@/hooks/useUser';
import { setModalState } from '@/redux/actions/uiActions/modalActions';
import { activeAppAction } from '@/screens/apps/redux/selectors/appScreenSelectors';
import { activeWorkflowStepAppAction } from '@/screens/workflows/redux/selectors/workflowScreenSelectors';
import { mockTypesMap } from '@shared/schema/mockTypesMap.js';
import { camelToTitle } from '@shared/utils/camelToTitle.js';
import { isValidElement, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Tag from './Tag';
import ToggleIndicator from './ToggleIndicator';
import { FiChevronDown } from './react-icons/main';
import IconV2 from './v2/IconV2.jsx';

// Caps how many object keys / array items render before the "N items hidden" expander. Rendering is synchronous, so unbounded payloads freeze the tab.
const safeLimit = 300;

const HiddenItemsRow = ({ level, hiddenCount, onExpand }) => (
  <div className={`row limit-message ${level ? `indented level-${level}` : ''}`}>
    <div className="value" onClick={onExpand}>
      <span className="limit-text">
        {hiddenCount} {hiddenCount === 1 ? 'item' : 'items'} hidden
        <IconV2 icon={FiChevronDown} />
      </span>
    </div>
  </div>
);

const KeyValue = ({ label, children, onClick, shouldIncrement = false }) => {
  const realLabel = isNaN(label) ? camelToTitle(label) : shouldIncrement ? parseInt(label) + 1 : parseInt(label);
  return (
    <div className="key" onClick={onClick} data-title={label}>
      {children}
      <strong>{realLabel}</strong>:
    </div>
  );
};

const DataViewer = ({ data, loading, theme = 'light', collapsed = 2, search }) => {
  const [openKeys, setOpenKeys] = useState({});
  const [expandedLimits, setExpandedLimits] = useState({});
  const { jwt } = useUser();
  const mockDataTypes = useSelector((state) => state?.settings?.mockDataTypes);
  const workflowScreenAppActionAttributes = useSelector((state) => activeWorkflowStepAppAction(state)?.attributes);
  const appScreenAppActionAttributes = useSelector((state) => activeAppAction(state)?.attributes);

  const isAdmin = jwt?.admin;
  const customMockTypes = useMemo(() => {
    const attributes = window.location.pathname.includes('/workflows') ? workflowScreenAppActionAttributes : appScreenAppActionAttributes;

    return attributes?.filter((n) => !mockTypesMap[n?.mockType]);
  }, [workflowScreenAppActionAttributes, appScreenAppActionAttributes]);

  // Reset user toggles when the payload changes. Nodes above `collapsed` depth default open without needing seeded entries (see isOpen in renderObject/renderArray). Stale expandedLimits would render another payload's arrays unbounded, so they reset too.
  useEffect(() => {
    setOpenKeys({});
    setExpandedLimits({});
  }, [data, collapsed]);

  const toggleOpen = (key, isOpen) => {
    setOpenKeys((prevOpenKeys) => ({
      ...prevOpenKeys,
      [key]: !isOpen
    }));
  };

  const toggleExpandedLimit = (key) => {
    setExpandedLimits((prevExpandedLimits) => ({
      ...prevExpandedLimits,
      [key]: !prevExpandedLimits[key]
    }));
  };

  const renderValue = (value) => {
    if (!search || !value.toLowerCase().includes(search.toLowerCase())) {
      return value;
    }

    const parts = value.split(new RegExp(`(${search})`, 'gi'));

    return (
      <span>
        {parts.map((part, index) => {
          const isHighlight = part.toLowerCase() === search.toLowerCase();

          if (isHighlight) {
            return (
              <span key={index} className="output-highlight">
                {part}
              </span>
            );
          }

          return part;
        })}
      </span>
    );
  };

  const renderObject = (obj, parentKey = '', level = 0) => {
    if (!obj) {
      return;
    }

    if (typeof obj === 'string') {
      return (
        <div className={`row string ${level ? `indented level-${level}` : ''}`}>
          <div className={`value`}>{renderValue(obj)}</div>
        </div>
      );
    }

    const filteredKeys = Object.keys(obj).filter((n) => n !== '__meta');
    const isLimited = safeLimit < filteredKeys.length;
    const isExpanded = expandedLimits[parentKey];
    const keysToRender = isExpanded ? filteredKeys : filteredKeys.slice(0, safeLimit);
    const hiddenCount = filteredKeys.length - safeLimit;

    const renderedKeys = keysToRender.map((key) => {
      const isReactElement = isValidElement(obj[key]);
      if (isReactElement) {
        return (
          <div key={key} className={`row ${level ? `indented level-${level}` : ''}`}>
            <KeyValue label={key} />
            <div className={`value`}>{obj[key]}</div>
          </div>
        );
      }
      const isObject = typeof obj[key] === 'object' && !Array.isArray(obj[key]) && obj[key] !== null && !(obj[key] instanceof Date);
      const isArray = Array.isArray(obj[key]);
      const dataKey = parentKey ? `${parentKey}.${key}` : key;
      const isOpen = level < collapsed ? openKeys[dataKey] !== false : openKeys[dataKey] === true;
      const isEmpty = obj[key] === null || obj[key] === undefined || (isObject && Object.keys(obj[key]).length === 0) || (isArray && obj[key].length === 0);
      const dataLength = isObject ? Object.keys(obj[key]).length : isArray ? obj[key].length : 0;

      const mockType = customMockTypes?.find((n) => n?.key === key);
      const fullMockType = mockDataTypes?.find((n) => n?.id === mockType?.mockType);

      const openMockTypeModal = () => {
        setModalState('mockDataTypes', { open: true, initialSelectedId: mockType?.mockType });
      };

      return (
        <div key={key} className={`row ${isObject ? 'object' : isArray ? 'array' : 'string'} ${level ? `indented level-${level}` : ''} ${isOpen ? 'open' : ''} ${isEmpty ? 'empty' : ''}`}>
          <KeyValue label={key} shouldIncrement={isArray} onClick={() => (isObject || isArray ? toggleOpen(dataKey, isOpen) : null)}>
            {(isObject || isArray) && <ToggleIndicator isOpen={isOpen} />}
          </KeyValue>
          {isEmpty && <span className={`value empty`}>{`(empty)`}</span>}
          {!isEmpty && (
            <div className={`value`}>
              {!isOpen && (isObject || isArray) && `(${dataLength})`}
              {isOpen && isObject && renderObject(obj[key], dataKey, level + 1)}
              {isOpen && isArray && renderArray(obj[key], dataKey, level + 1)}
              {!(isObject || isArray) && renderValue(`${obj[key]}`)}
              {isAdmin && fullMockType && (
                <Tag variant={'info'} onClick={openMockTypeModal}>
                  {fullMockType?.name}
                </Tag>
              )}
            </div>
          )}
        </div>
      );
    });

    if (!isLimited || isExpanded) {
      return renderedKeys;
    }

    return [...renderedKeys, <HiddenItemsRow key={`${parentKey || '__root'}-limit-message`} level={level} hiddenCount={hiddenCount} onExpand={() => toggleExpandedLimit(parentKey)} />];
  };

  const renderArray = (arr, parentKey, level = 0) => {
    if (!arr) {
      return;
    }

    const isLimited = safeLimit < arr.length;
    const isExpanded = expandedLimits[parentKey];
    const itemsToRender = isExpanded ? arr : arr.slice(0, safeLimit);
    const hiddenCount = arr.length - safeLimit;

    const renderedItems = itemsToRender.map((item, index) => {
      const key = parentKey ? `${parentKey}[${index}]` : index;
      const isObject = typeof item === 'object' && !Array.isArray(item) && item !== null;
      const isArray = Array.isArray(item);
      const isOpen = level < collapsed ? openKeys[key] !== false : openKeys[key] === true;
      const isEmpty = item === null || item === undefined || (isObject && Object.keys(item).length === 0) || (isArray && item.length === 0);
      const dataLength = isObject ? Object.keys(item).length : isArray ? item.length : 0;

      return (
        <div key={key} className={`row ${isObject ? 'object' : isArray ? 'array' : 'string'} ${level ? `indented level-${level}` : ''} ${isOpen ? 'open' : ''} ${isEmpty ? 'empty' : ''}`}>
          <KeyValue label={index} onClick={() => (isObject || isArray ? toggleOpen(key, isOpen) : null)}>
            {isObject && <ToggleIndicator isOpen={isOpen} />}
          </KeyValue>
          {isEmpty && <span className={`value empty`}>{`(empty)`}</span>}
          {!isEmpty && (
            <div className={`value`}>
              {!isOpen && (isObject || isArray) && `(${dataLength})`}
              {isOpen && isObject && renderObject(item, key, level + 1)}
              {isOpen && isArray && renderArray(item, key, level + 1)}
              {!isObject && renderValue(`${item}`)}
            </div>
          )}
        </div>
      );
    });

    if (!isLimited || isExpanded) {
      return renderedItems;
    }

    return [...renderedItems, <HiddenItemsRow key={`${parentKey}-limit-message`} level={level} hiddenCount={hiddenCount} onExpand={() => toggleExpandedLimit(parentKey)} />];
  };

  return (
    <div className={`comp-data-viewer ${theme}`}>
      {loading && <div className={'loading'}>Loading...</div>}
      {!loading && renderObject(data)}
    </div>
  );
};

export default DataViewer;
